import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import {
  Loader2, Plus, RefreshCw, Wrench, Palette, ShieldCheck, ShieldAlert,
  UserCircle2, Tag as TagIcon, Calendar as CalendarIcon, Award, AlertTriangle,
  Search, Filter, Pencil, Send, GitPullRequestArrow, ClipboardCheck, MessageSquare, Link2,
} from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { Input } from './ui/input';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Visual config per action type — icon, soft bg, border, label for filters.
// Kept as a single map so adding a new action_type only touches one place.
const ACTION_VISUALS = {
  TASK_CREATED:        { Icon: Plus,            tone: 'green',  label: 'Task Created' },
  TASK_UPDATED:        { Icon: Pencil,          tone: 'slate',  label: 'Task Updated' },
  STATUS_CHANGED:      { Icon: RefreshCw,       tone: 'blue',   label: 'Status Changed' },
  ASSIGNED_CHANGED:    { Icon: UserCircle2,     tone: 'blue',   label: 'Assignment Changed' },
  DEVELOPER_UPDATED:   { Icon: Wrench,          tone: 'orange', label: 'Developer Update' },
  UIUX_UPDATED:        { Icon: Palette,         tone: 'purple', label: 'UI/UX Update' },
  MANAGER_APPROVED:    { Icon: ShieldCheck,     tone: 'green',  label: 'Manager Approved' },
  MANAGER_REJECTED:    { Icon: ShieldAlert,     tone: 'red',    label: 'Manager Rejected' },
  SUBMITTED_FOR_APPROVAL: { Icon: Send,          tone: 'indigo', label: 'Submitted for Approval' },
  APPROVAL_STATUS_CHANGED: { Icon: ClipboardCheck, tone: 'yellow', label: 'Approval Status Changed' },
  TASK_RESUBMITTED:    { Icon: GitPullRequestArrow, tone: 'indigo', label: 'Resubmitted for Approval' },
  COMMENT_ADDED:       { Icon: MessageSquare,   tone: 'slate',  label: 'Comment / Note' },
  LINK_AUTO_ADDED:     { Icon: Link2,           tone: 'slate',  label: 'Link Auto-Added' },
  DUE_DATE_CHANGED:    { Icon: CalendarIcon,    tone: 'blue',   label: 'Due Date Changed' },
  START_DATE_CHANGED:  { Icon: CalendarIcon,    tone: 'blue',   label: 'Start Date Changed' },
  BRAND_CHANGED:       { Icon: Award,           tone: 'blue',   label: 'Brand Changed' },
  CATEGORY_CHANGED:    { Icon: TagIcon,         tone: 'blue',   label: 'Category Changed' },
  PRIORITY_CHANGED:    { Icon: AlertTriangle,   tone: 'blue',   label: 'Priority Changed' },
};

const TONE_CLASSES = {
  green:  { ring: 'bg-green-100 text-green-700 ring-green-200',   bar: 'bg-green-200' },
  blue:   { ring: 'bg-blue-100 text-blue-700 ring-blue-200',      bar: 'bg-blue-200' },
  orange: { ring: 'bg-orange-100 text-orange-700 ring-orange-200',bar: 'bg-orange-200' },
  purple: { ring: 'bg-purple-100 text-purple-700 ring-purple-200',bar: 'bg-purple-200' },
  yellow: { ring: 'bg-amber-100 text-amber-700 ring-amber-200',   bar: 'bg-amber-200' },
  red:    { ring: 'bg-red-100 text-red-700 ring-red-200',         bar: 'bg-red-200' },
  slate:  { ring: 'bg-slate-100 text-slate-700 ring-slate-200',   bar: 'bg-slate-200' },
  indigo: { ring: 'bg-indigo-100 text-indigo-700 ring-indigo-200',bar: 'bg-indigo-200' },
};

const renderTimestamp = (iso) => {
  try {
    return format(parseISO(iso), 'MMM d, yyyy • h:mm a');
  } catch (_) {
    return iso || '';
  }
};

// Replace user_id references in description with full names where possible
const hydrateAssigneeNames = (event, usersById) => {
  if (event.action_type !== 'ASSIGNED_CHANGED') return event;
  const oldName = usersById.get(event.old_value)?.full_name;
  const newName = usersById.get(event.new_value)?.full_name;
  // Backend description may embed raw user IDs ("from '<id>' to '<id>'") — swap
  // them in-place with the resolved names so the headline reads naturally.
  let description = event.description || '';
  if (event.old_value && oldName) {
    description = description.split(event.old_value).join(oldName);
  }
  if (event.new_value && newName) {
    description = description.split(event.new_value).join(newName);
  }
  return {
    ...event,
    description,
    old_value: oldName || event.old_value || '—',
    new_value: newName || event.new_value || '—',
  };
};

// Pretty-print ISO timestamps that come through as raw strings inside diffs
// (e.g. start_date / due_date changes). Falls back to the original string.
const prettifyMaybeDate = (val) => {
  if (!val || typeof val !== 'string') return val;
  // Heuristic: anything that looks ISO-ish goes through date-fns
  if (/^\d{4}-\d{2}-\d{2}T/.test(val)) {
    try { return format(parseISO(val), 'MMM d, yyyy'); } catch (_) { return val; }
  }
  return val;
};

const TaskTimeline = ({ taskId, users = [] }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionTypeFilter, setActionTypeFilter] = useState('all');
  const [userFilter, setUserFilter] = useState('all');
  const [search, setSearch] = useState('');

  const usersById = useMemo(() => new Map(users.map((u) => [u.id, u])), [users]);
  const token = (() => {
    try { return localStorage.getItem('token'); } catch (_) { return null; }
  })();

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!taskId) return;
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(`${API}/tasks/${taskId}/activity`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setEvents(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || 'Failed to load activity');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [taskId, token]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return events
      .filter((e) => actionTypeFilter === 'all' || e.action_type === actionTypeFilter)
      .filter((e) => userFilter === 'all' || e.user_id === userFilter)
      .filter((e) => !q || (e.description || '').toLowerCase().includes(q))
      .map((e) => hydrateAssigneeNames(e, usersById));
  }, [events, actionTypeFilter, userFilter, search, usersById]);

  const uniqueActors = useMemo(() => {
    const seen = new Map();
    events.forEach((e) => {
      if (e.user_id && !seen.has(e.user_id)) seen.set(e.user_id, e.user_name || 'Unknown');
    });
    return [...seen.entries()];
  }, [events]);

  // Action-type filter only lists types that actually appear in this task's log.
  const availableActionTypes = useMemo(() => {
    const set = new Set(events.map((e) => e.action_type));
    return [...set];
  }, [events]);

  return (
    <div className="space-y-4" data-testid="task-timeline">
      {/* Filter row */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[180px]">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search activity..."
            className="h-9 pl-8 text-sm"
            data-testid="timeline-search"
          />
        </div>
        <Select value={actionTypeFilter} onValueChange={setActionTypeFilter}>
          <SelectTrigger className="h-9 w-[170px] text-sm" data-testid="timeline-action-filter">
            <Filter size={13} className="mr-1 text-slate-500" />
            <SelectValue placeholder="All Activities" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Activities</SelectItem>
            {availableActionTypes.map((t) => (
              <SelectItem key={t} value={t}>{ACTION_VISUALS[t]?.label || t}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={userFilter} onValueChange={setUserFilter}>
          <SelectTrigger className="h-9 w-[160px] text-sm" data-testid="timeline-user-filter">
            <SelectValue placeholder="All Users" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Users</SelectItem>
            {uniqueActors.map(([id, name]) => (
              <SelectItem key={id} value={id}>{name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Feed */}
      {loading ? (
        <div className="flex items-center justify-center py-10 text-slate-500" data-testid="timeline-loading">
          <Loader2 className="animate-spin mr-2" size={16} /> Loading activity…
        </div>
      ) : error ? (
        <div className="p-4 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">{error}</div>
      ) : filtered.length === 0 ? (
        <div className="p-8 rounded-lg border-2 border-dashed border-slate-200 bg-slate-50/50 text-center text-sm text-slate-500">
          {events.length === 0 ? 'No activity logged yet.' : 'No activity matches your filters.'}
        </div>
      ) : (
        <ol className="relative pl-7 space-y-3" data-testid="timeline-feed">
          {/* The vertical rail behind every icon */}
          <span aria-hidden="true" className="absolute left-3 top-2 bottom-2 w-px bg-slate-200" />
          {filtered.map((event) => {
            const cfg = ACTION_VISUALS[event.action_type] || ACTION_VISUALS.TASK_UPDATED;
            const tone = TONE_CLASSES[cfg.tone] || TONE_CLASSES.slate;
            const Icon = cfg.Icon;
            const showDiff = event.old_value || event.new_value;
            return (
              <li key={event.id} className="relative" data-testid={`timeline-event-${event.action_type}`}>
                <span
                  aria-hidden="true"
                  className={`absolute -left-7 top-2 inline-flex h-6 w-6 items-center justify-center rounded-full ring-2 ring-white ${tone.ring}`}
                >
                  <Icon size={12} />
                </span>
                <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm hover:border-slate-300 transition-colors">
                  <div className="flex items-start justify-between gap-3">
                    <div className="text-sm font-medium text-slate-800 break-words">
                      {event.description}
                      {event.user_role && (
                        <span
                          className="ml-2 align-middle text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200"
                          data-testid={`timeline-role-${event.action_type}`}
                        >
                          {event.user_role}
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] whitespace-nowrap text-slate-400">
                      {renderTimestamp(event.created_at)}
                    </div>
                  </div>
                  {showDiff && event.action_type !== 'TASK_CREATED' && (
                    <div className="mt-1.5 text-xs text-slate-600">
                      <span className="line-through opacity-60">{prettifyMaybeDate(event.old_value) || '—'}</span>
                      <span className="mx-1.5 text-slate-400">→</span>
                      <span className={`font-medium ${tone.ring} px-1.5 py-0.5 rounded`}>{prettifyMaybeDate(event.new_value) || '—'}</span>
                    </div>
                  )}
                  {event.metadata?.notes && (
                    <div
                      className="mt-2 flex items-start gap-1.5 rounded-md bg-slate-50 border border-slate-200 px-2.5 py-1.5 text-xs text-slate-700"
                      data-testid={`timeline-note-${event.action_type}`}
                    >
                      <MessageSquare size={12} className="mt-0.5 shrink-0 text-slate-400" />
                      <span className="break-words italic">{event.metadata.notes}</span>
                    </div>
                  )}
                  {event.action_type === 'TASK_CREATED' && event.metadata && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {Object.entries(event.metadata)
                        .filter(([, v]) => v)
                        .map(([k, v]) => {
                          const display = k === 'assigned_to' ? (usersById.get(v)?.full_name || v) : v;
                          return (
                            <span key={k} className="text-[11px] px-2 py-0.5 rounded-md bg-slate-100 text-slate-700 border border-slate-200">
                              {k.replace(/_/g, ' ')}: {display}
                            </span>
                          );
                        })}
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
};

export default TaskTimeline;
